
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      if (savedPreferences.analytics) {
        loadGoogleAnalytics();
      }
    }
  }, []);

  const loadGoogleAnalytics = () => {
    // Google Analytics 4 - Placeholder ID (replace with actual ID)
    const GA_ID = 'G-XXXXXXXXXX';
    
    // Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure'
    });
  };

  const acceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setPreferences(newPreferences);
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    loadGoogleAnalytics();
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setPreferences(newPreferences);
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setShowBanner(false);
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    if (preferences.analytics) {
      loadGoogleAnalytics();
    }
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto">
          {!showSettings ? (
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {t('common:cookies.title')}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('common:cookies.description')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('common:cookies.settings')}
                </button>
                <button
                  onClick={acceptNecessary}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('common:cookies.necessaryOnly')}
                </button>
                <button
                  onClick={acceptAll}
                  className="px-5 py-2.5 text-sm font-medium text-black bg-amber-500 rounded-full hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('common:cookies.acceptAll')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('common:cookies.settingsTitle')}
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {t('common:cookies.necessary.title')}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {t('common:cookies.necessary.description')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-amber-500 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-1 block text-center">
                      {t('common:cookies.alwaysOn')}
                    </span>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {t('common:cookies.analytics.title')}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {t('common:cookies.analytics.description')}
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                    className={`ml-4 w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                      preferences.analytics ? 'bg-amber-500 justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {t('common:cookies.marketing.title')}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {t('common:cookies.marketing.description')}
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                    className={`ml-4 w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                      preferences.marketing ? 'bg-amber-500 justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={acceptNecessary}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('common:cookies.necessaryOnly')}
                </button>
                <button
                  onClick={savePreferences}
                  className="px-5 py-2.5 text-sm font-medium text-black bg-amber-500 rounded-full hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('common:cookies.savePreferences')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
